import React, { useState, useRef, useEffect } from "react";
import {
  SiReact,
  SiNextdotjs,
  SiTypescript,
  SiTailwindcss,
  SiPython,
  SiFlask,
  SiFigma,
  SiFramer,
  SiHotjar,
  SiMaze,
  SiDovetail,
} from "react-icons/si";
import "../styles/homePage.css";
import Navbar from "../components/navbar.js";
import Footer from "../components/footer.js";
import { useLocation, useNavigate } from "react-router-dom";

export default function HomePage() {
  const [lang, setLang] = useState("EN");
  // const [showAbout, setShowAbout] = useState(false);
  const revealRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 100, y: 100 });
  const location = useLocation();
  const navigate = useNavigate();
  const [isHeroHidden, setIsHeroHidden] = useState(false);
  const [isAtRevealBottom, setIsAtRevealBottom] = useState(false);
  const progressRef = useRef(0);
  const isScrollingRef = useRef(false);
  const touchStartYRef = useRef(0);
  const lastScrollYRef = useRef(0);
  // eslint-disable-next-line no-unused-vars
  const [isMobile, setIsMobile] = useState(false);
  const [openDesignStep, setOpenDesignStep] = useState(0);
  const [openDevStep, setOpenDevStep] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // // Check if we need to scroll to contact section
  // useEffect(() => {
  //   const urlParams = new URLSearchParams(location.search);
  //   const scrollToContact = urlParams.get("scrollToContact");

  //   if (scrollToContact === "true") {
  //     // Hide hero immediately and show full content
  //     setIsHeroHidden(true);
  //     progressRef.current = 1;
  //     if (revealRef.current) {
  //       revealRef.current.style.clipPath = "circle(100% at 50% 100%)";
  //     }

  //     setTimeout(() => {
  //       const contactElement = document.querySelector(".contact");
  //       if (contactElement) {
  //         contactElement.scrollIntoView({ behavior: "smooth" });
  //       }
  //     }, 500);
  //   }
  // }, [location]);

  // Mouse tracking for flashlight effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Signal that HomePage hero animations are loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!document.body.classList.contains("hero-animations-loaded")) {
        // Import the signalAnimationReady function for better coordination
        const signalReady = () => {
          document.body.classList.add("hero-animations-loaded");
          window.dispatchEvent(new CustomEvent("heroAnimationsReady"));
        };

        signalReady();
      }
    }, 500); // Give time for CSS animations to initialize

    return () => clearTimeout(timer);
  }, []);

  // Handle scroll and touch for revealing content
  useEffect(() => {
    const handleScroll = (deltaY) => {
      if (isScrollingRef.current) return;

      isScrollingRef.current = true;

      // If hero is already hidden, let normal scrolling happen
      if (isHeroHidden) {
        // Don't allow scrolling up to reveal hero
        if (deltaY < 0) {
          isScrollingRef.current = false;
          return;
        }
        isScrollingRef.current = false;
        return;
      }

      // Only process downward scrolls when hero is visible
      if (deltaY <= 0) {
        isScrollingRef.current = false;
        return;
      }

      // Update progress based on scroll (only downward)
      progressRef.current += deltaY * 0.01;
      progressRef.current = Math.min(progressRef.current, 1);

      // Update clip-path
      if (revealRef.current) {
        const circleSize = progressRef.current * 200;
        revealRef.current.style.clipPath = `circle(${circleSize}% at 50% 100%)`;

        // When hero is fully hidden
        if (progressRef.current >= 1) {
          setIsHeroHidden(true);
          document.body.style.overflow = "auto";
        }
      }

      // Reset scrolling flag after a short delay
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 50);
    };

    // Handle mouse wheel
    const handleWheel = (e) => {
      e.preventDefault();
      handleScroll(e.deltaY);
    };

    // Handle touch events for mobile
    const handleTouchStart = (e) => {
      touchStartYRef.current = e.touches[0].clientY;
      lastScrollYRef.current = window.scrollY;
    };

    const handleTouchMove = (e) => {
      // If hero is hidden, let normal scrolling happen
      if (isHeroHidden) {
        if (
          window.scrollY <= 0 &&
          e.touches[0].clientY > touchStartYRef.current
        ) {
          e.preventDefault();
        }
        return;
      }

      // Only process if hero is still visible
      e.preventDefault();
      const touchY = e.touches[0].clientY;
      const deltaY = touchStartYRef.current - touchY;
      touchStartYRef.current = touchY;

      // Only process downward touch movements
      if (deltaY > 0) {
        handleScroll(deltaY * 10);
      }
    };

    // Prevent overscroll bounce that could reveal hero
    const handleTouchEnd = () => {
      if (isHeroHidden && window.scrollY === 0) {
        setTimeout(() => {
          if (window.scrollY < 10) {
            window.scrollTo(0, 10);
          }
        }, 100);
      }
    };

    // Add event listeners
    if (!isHeroHidden) {
      window.addEventListener("wheel", handleWheel, { passive: false });
      window.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isHeroHidden]);

  // Show mobile nav only when scrolled to the bottom of the reveal-section
  useEffect(() => {
    if (!isHeroHidden || !revealRef.current) return;

    const el = revealRef.current;
    const handleScroll = () => {
      setIsAtRevealBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 10);
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [isHeroHidden]);

  useEffect(() => {
    // Check if URL has a hash
    if (location.hash) {
      const elementId = location.hash.substring(1);
      const element = document.getElementById(elementId);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  // Update event listeners when hero state changes
  useEffect(() => {
    if (isHeroHidden) {
      document.body.style.overflow = "auto";

      // Prevent overscroll at the top
      const handleScroll = () => {
        if (window.scrollY <= 0) {
          window.scrollTo(0, 1);
        }
      };

      window.addEventListener("scroll", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    } else {
      // On mobile, avoid locking the body to keep the fixed bottom nav visible
      if (isMobile) {
        document.body.style.overflowX = "hidden";
        document.body.style.overflowY = "auto";
      } else {
        document.body.style.overflow = "hidden";
      }
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isHeroHidden, isMobile]);

  return (
    <>
      {/* Keep navbar outside the reveal clip so mobile bottom nav stays visible */}
      <Navbar lang={lang} setLang={setLang} showMobileNav={isAtRevealBottom} />
      <section className="hero">
        <div id="hero-overlay">
          {/* Floating Moon */}
          <div className="floating-moon"></div>
          <div className="hero-content">
            <h1 className="fugaz-one-bold">YORU SOLUTIONS</h1>
            <p className="geist-mono-regular">
              {lang === "EN"
                ? "Product Design /Web Development & Copywriting / Translation"
                : "Design Produit / Développement Web & Rédaction / Traduction"}
            </p>

            <p className="geist-mono-light">
              {lang === "EN"
                ? "(scroll down to explore)"
                : "(faites défiler vers le bas pour explorer)"}
            </p>

            {/* Pulsing icon */}
            <div className="pulsing-icon">
              <div className="pulse-line"></div>
              <div className="pulse-arrow">↓</div>
            </div>
          </div>
        </div>

        {/* Reveal section with flashlight effect */}
        <div
          className={`reveal-section ${isHeroHidden ? "hero-hidden" : ""}`}
          ref={revealRef}
          style={{
            "--mouse-x": `${mousePosition.x}%`,
            "--mouse-y": `${mousePosition.y}%`,
          }}
        >
          <section id="Header" className="Header">
            <h1>
              {lang === "EN"
                ? "Bridging the Gap Between Code and Connection"
                : "Combler le Fossé entre le Code et la Connexion"}
            </h1>
            <h2 className="home-intro">
              {lang === "EN" ? (
                <>
                  Specializing in software that captures your attention and
                  storytelling that resonates with the collective human
                  experience.
                  <span className="line-break"></span>
                  Nostalgic by nature, modern by design.
                </>
              ) : (
                <>
                  Nous nous spécialisons dans des logiciels qui captivent
                  l'attention et un storytelling qui résonne avec l'expérience
                  humaine collective.
                  <span className="line-break"></span>
                  Nostalgique par nature, moderne par conception.
                </>
              )}
            </h2>
          </section>

          {/* Rotating Cards Section */}
          <div className="rotating-cards-section">
            <div className="rotating-cards-container">
              {/* Rotating Card 0 */}
              <div className="rotating-card">
                <div className="card-inner">
                  <div className="card-front">
                    <h3>
                      {lang === "EN" ? "Product Design" : "Design Produit"}
                    </h3>
                  </div>
                  <div className="card-back">
                    <p>
                      {lang === "EN"
                        ? "From wireframes to polished interfaces, we design intuitive, visually compelling products that bridge brand identity and seamless user experience."
                        : "Des maquettes aux interfaces finalisées, nous concevons des produits intuitifs et visuellement percutants qui allient identité de marque et expérience utilisateur fluide."}
                    </p>
                  </div>
                </div>
              </div>
              {/* Rotating Card 1 */}
              <div className="rotating-card">
                <div className="card-inner">
                  <div className="card-front">
                    <h3>
                      {lang === "EN" ? "Web Development" : "Développement Web"}
                    </h3>
                  </div>
                  <div className="card-back">
                    <p>
                      {lang === "EN"
                        ? "Bespoke digital foundations. We build fast, reliable, and scalable experiences (React, Next.js) that fit the feel and spirit of your company's mission."
                        : "Des fondations numériques sur mesure. Nous bâtissons des expériences rapides, fiables et évolutives (React, Next.js) qui s'accordent avec l'essence et l'esprit de la mission de votre entreprise."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Rotating Card 2 */}
              <div className="rotating-card">
                <div className="card-inner">
                  <div className="card-front">
                    <h3>{lang === "EN" ? "Copywriting" : "Rédaction"}</h3>
                  </div>
                  <div className="card-back">
                    <p>
                      {lang === "EN"
                        ? "Compelling copy that carries the nostalgia of culture and time, modernized for today's scroll-stopping consumers."
                        : "Un récit captivant, empreint de nostalgie et d'intemporalité, modernisé pour l'audience d'aujourd'hui."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Rotating Card 3 */}
              <div className="rotating-card">
                <div className="card-inner">
                  <div className="card-front">
                    <h3>{lang === "EN" ? "Translation" : "Traduction"}</h3>
                  </div>
                  <div className="card-back">
                    <p>
                      {lang === "EN"
                        ? "Beyond words: We translate your brand's essence and tone, ensuring your message lands perfectly on the international stage."
                        : "Au-delà des mots : Nous traduisons l'essence et le ton de votre marque, assurant que votre message porte parfaitement sur la scène internationale."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="navy-card-bg">
            <div className="stack">
              {/* Row 1 — Product Design: process text left, tools right */}
              <div className="stack-row">
                <div className="stack-text-col">
                  <h2 className="stack-row-heading">
                    {lang === "EN" ? "Product Design" : "Design Produit"}
                  </h2>
                  <div className="process-flow">
                    {[
                      {
                        num: "01",
                        en: "Market Research",
                        fr: "Étude de Marché",
                        bodyEn:
                          "We study your industry, competitors, and target audience — grounding every design decision in real data before a single element is placed.",
                        bodyFr:
                          "Nous étudions votre secteur, vos concurrents et votre audience pour ancrer chaque décision dans des données réelles.",
                      },
                      {
                        num: "02",
                        en: "Wireframing",
                        fr: "Maquettage",
                        bodyEn:
                          "Low-fidelity blueprints map out structure and user flow — locking in the architecture before any visual polish is applied.",
                        bodyFr:
                          "Des maquettes basse-fidélité structurent les flux et l'architecture avant d'appliquer le moindre détail visuel.",
                      },
                      {
                        num: "03",
                        en: "Prototyping",
                        fr: "Prototypage",
                        bodyEn:
                          "Clickable prototypes validate the user journey and surface friction early — before costly development begins.",
                        bodyFr:
                          "Des prototypes interactifs valident le parcours utilisateur et révèlent les frictions avant le développement.",
                      },
                      {
                        num: "04",
                        en: "Data Analysis",
                        fr: "Analyse des Données",
                        bodyEn:
                          "Post-launch behavior data feeds back into the product — continuous iteration driven by what users actually do, not what they say.",
                        bodyFr:
                          "Les données post-lancement alimentent le produit en continu — des itérations guidées par ce que font réellement les utilisateurs.",
                      },
                    ].map((step, i) => (
                      <div className="process-step" key={step.num}>
                        <button
                          className={`step-header ${openDesignStep === i ? "open" : ""}`}
                          onClick={() =>
                            setOpenDesignStep(openDesignStep === i ? null : i)
                          }
                        >
                          <span className="step-number">{step.num}</span>
                          <h4>{lang === "EN" ? step.en : step.fr}</h4>
                          <span className="step-chevron">
                            {openDesignStep === i ? "−" : "+"}
                          </span>
                        </button>
                        <div
                          className={`step-body ${openDesignStep === i ? "open" : ""}`}
                        >
                          <p>{lang === "EN" ? step.bodyEn : step.bodyFr}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="stack-cards-col">
                  <div className="tech-stack-container">
                    <div className="tech-item">
                      <SiFigma className="tech-icon" />
                      <span>Figma</span>
                    </div>
                    <div className="tech-item">
                      <SiFramer className="tech-icon" />
                      <span>Framer</span>
                    </div>
                    <div className="tech-item">
                      <SiHotjar className="tech-icon" />
                      <span>Hotjar</span>
                    </div>
                    <div className="tech-item">
                      <SiMaze className="tech-icon" />
                      <span>Maze</span>
                    </div>
                    <div className="tech-item">
                      <SiDovetail className="tech-icon" />
                      <span>Dovetail</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="navy-card-bg">
            {/* Row 2 — Dev + Security: tools left, process text right */}
            <div className="stack-row">
              <div className="stack-cards-col">
                <div className="tech-stack-container">
                  <div className="tech-item">
                    <SiReact className="tech-icon" />
                    <span>React</span>
                  </div>
                  <div className="tech-item">
                    <SiNextdotjs className="tech-icon" />
                    <span>Next.js</span>
                  </div>
                  <div className="tech-item">
                    <SiTypescript className="tech-icon" />
                    <span>TypeScript</span>
                  </div>
                  <div className="tech-item">
                    <SiTailwindcss className="tech-icon" />
                    <span>Tailwind</span>
                  </div>
                  <div className="tech-item">
                    <SiPython className="tech-icon" />
                    <span>Python</span>
                  </div>
                  <div className="tech-item">
                    <SiFlask className="tech-icon" />
                    <span>Flask</span>
                  </div>
                </div>
              </div>
              <div className="stack-text-col">
                <h2 className="stack-row-heading">
                  {lang === "EN"
                    ? "Development & Security"
                    : "Développement & Sécurité"}
                </h2>
                <div className="process-flow">
                  {[
                    {
                      num: "01",
                      en: "Architecture",
                      fr: "Architecture",
                      bodyEn:
                        "We design scalable systems from the ground up — selecting the right tech for your performance, growth, and maintainability goals.",
                      bodyFr:
                        "Nous concevons des systèmes évolutifs dès la base, en choisissant les technologies adaptées à vos objectifs de performance et de croissance.",
                    },
                    {
                      num: "02",
                      en: "Development",
                      fr: "Développement",
                      bodyEn:
                        "Clean, tested code built on modern frameworks that perform under real-world load — no shortcuts, no technical debt handed to you.",
                      bodyFr:
                        "Du code propre et testé sur des frameworks modernes, conçu pour tenir sous une charge réelle — sans raccourcis ni dette technique.",
                    },
                    {
                      num: "03",
                      en: "Security Audit",
                      fr: "Audit de Sécurité",
                      bodyEn:
                        "We probe for vulnerabilities before attackers do — covering auth flows, data exposure, injection risks, and OWASP top 10 vectors.",
                      bodyFr:
                        "Nous détectons les vulnérabilités avant les attaquants — flux d'authentification, exposition des données, injections et vecteurs OWASP.",
                    },
                    {
                      num: "04",
                      en: "Deployment",
                      fr: "Déploiement",
                      bodyEn:
                        "CI/CD pipelines, edge caching, and real-time monitoring ensure your product ships fast, stays stable, and recovers instantly from failure.",
                      bodyFr:
                        "Pipelines CI/CD, mise en cache en périphérie et surveillance en temps réel garantissent un déploiement rapide, stable et résilient.",
                    },
                  ].map((step, i) => (
                    <div className="process-step" key={step.num}>
                      <button
                        className={`step-header ${openDevStep === i ? "open" : ""}`}
                        onClick={() =>
                          setOpenDevStep(openDevStep === i ? null : i)
                        }
                      >
                        <span className="step-number">{step.num}</span>
                        <h4>{lang === "EN" ? step.en : step.fr}</h4>
                        <span className="step-chevron">
                          {openDevStep === i ? "−" : "+"}
                        </span>
                      </button>
                      <div
                        className={`step-body ${openDevStep === i ? "open" : ""}`}
                      >
                        <p>{lang === "EN" ? step.bodyEn : step.bodyFr}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Button */}
          <div className="contact-section">
            <h1>
              {lang === "EN"
                ? "Let's discuss what you're looking for"
                : "Discutons de ce que vous recherchez"}
            </h1>
            <p className="contact-section-tagline geist-mono-heavy">
              {lang === "EN"
                ? "Your next project is our new obsession. Tell us your vision — we'll work our creative magic until it's real."
                : "Votre prochain projet est notre nouvelle obsession. Partagez votre vision — nous y mettrons toute notre magie créative jusqu'à ce qu'elle devienne réalité."}
            </p>
            <button
              className="contact-cta-btn"
              onClick={() => navigate("/ContactYoru")}
            >
              {lang === "EN" ? "Get In Touch" : "Nous Contacter"}
            </button>
          </div>

          <Footer lang={lang} visible={isAtRevealBottom} />
        </div>
      </section>
    </>
  );
}
